import React from 'react';
import { cn } from '../../utils';

interface CardProps {
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
  isSelected?: boolean;
  isHoverable?: boolean;
}

export const Card: React.FC<CardProps> = ({
  className,
  children,
  onClick,
  isSelected = false,
  isHoverable = true,
  ...props
}) => {
  return (
    <div
      className={cn(
        'rounded-lg border bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950',
        onClick && 'cursor-pointer',
        isHoverable && 'transition-all duration-200 hover:shadow-md',
        isSelected && 'ring-2 ring-blue-500 border-blue-200 dark:border-blue-800',
        className
      )}
      onClick={onClick}
      {...props}
    >
      {children}
    </div>
  );
};

interface CardHeaderProps {
  className?: string;
  children: React.ReactNode;
}

export const CardHeader: React.FC<CardHeaderProps> = ({ className, children }) => {
  return (
    <div className={cn('flex flex-col space-y-1.5 pb-4', className)}>
      {children}
    </div>
  );
};

interface CardTitleProps {
  className?: string;
  children: React.ReactNode;
}

export const CardTitle: React.FC<CardTitleProps> = ({ className, children }) => {
  return (
    <h3 className={cn('text-lg font-semibold leading-none tracking-tight text-slate-900 dark:text-white', className)}>
      {children}
    </h3>
  );
};

interface CardDescriptionProps {
  className?: string;
  children: React.ReactNode;
}

export const CardDescription: React.FC<CardDescriptionProps> = ({ className, children }) => {
  return (
    <p className={cn('text-sm text-slate-500 dark:text-slate-400', className)}>
      {children}
    </p>
  );
};

interface CardContentProps {
  className?: string;
  children: React.ReactNode;
}

export const CardContent: React.FC<CardContentProps> = ({ className, children }) => {
  return (
    <div className={cn('pt-0', className)}>
      {children}
    </div>
  );
};

interface CardFooterProps {
  className?: string;
  children: React.ReactNode;
}

export const CardFooter: React.FC<CardFooterProps> = ({ className, children }) => {
  return (
    <div className={cn('flex items-center pt-4', className)}>
      {children}
    </div>
  );
};